import React, { useEffect, useMemo, useState } from "react";
import { getMonthDays, getMonthName, getWeekDays } from "./CalendarLogic";
import { fetchEventCounts, gridRangeFromDays, fetchLockedDates, lockDate, unlockDate } from "./EventsApiCalls";
import "./Calendar.css";
import DaysEventsModal from "./dayeventsmodal/DayEventsModal";
import EventEditModal from "./dayeventsmodal/EventEditModal";
import ConfirmDialog from "../../../globalcontrols/confirmdialog/ConfirmDialog";

function Calendar({ filters, createRequest }) {
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());

  const days = useMemo(() => getMonthDays(currentYear, currentMonth), [currentYear, currentMonth]);
  const weekDays = getWeekDays();

  const [countByDay, setCountByDay] = useState({});
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [menuDate, setMenuDate] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [lockedMap, setLockedMap] = useState({}); // { 'YYYY-MM-DD': reason }
  const [lockDialog, setLockDialog] = useState({ open: false, date: null, reason: '' });
  const [confirmDialog, setConfirmDialog] = useState(null);

  const toISODate = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Display helper for titles: dd-mm-yyyy
  const toDMY = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  };

  const openDayModal = (date) => {
    setSelectedDate(date);
    setModalOpen(true);
  };

  const openMenuAt = (event, date, hasEvents) => {
    event.preventDefault();
    const x = event.clientX;
    const y = event.clientY;
    setMenuPos({ x, y });
    setMenuDate(date);
    setMenuOpen(true);
  };

  const requestUnlockDate = (date) => {
    const day = toKey(date);
    setConfirmDialog({
      title: "Desbloquear dia",
      message: `Quitar el bloqueo del dia ${toDMY(date)}?`,
      confirmLabel: "Desbloquear",
      onConfirm: async () => {
        try {
          await unlockDate(day);
          setLockedMap((current) => {
            const next = { ...current };
            delete next[day];
            return next;
          });
        } catch (e) {
          alert(e.message);
        } finally {
          setConfirmDialog(null);
        }
      },
    });
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = () => setMenuOpen(false);
    const onEsc = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    // Use 'click' instead of 'mousedown' so button onClick can fire before menu closes
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpen]);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDate(null);
  };

  useEffect(() => {
    const { from, to } = gridRangeFromDays(days);
    if (!from || !to) return;

    const controller = new AbortController();
    setLoading(true);

    fetchEventCounts({ from, to, filters, signal: controller.signal })
      .then((counts) => setCountByDay(counts))
      .catch((err) => {
        if (err?.name !== "AbortError") {
          console.error("fetchEventCounts error:", err);
        }
      })
      .finally(() => setLoading(false));

    // Load locked dates for the visible grid
    fetchLockedDates({ from, to, signal: controller.signal })
      .then((rows)=>{
        const map = {};
        for (const r of rows) {
          const raw = r?.day;
          const key = typeof raw === 'string' ? raw.slice(0,10) : toISODate(new Date(raw));
          if (key) map[key] = r.reason || '';
        }
        setLockedMap(map);
      })
      .catch((err)=>{
        if (err?.name !== 'AbortError') console.error('fetchLockedDates error:', err);
      });

    // Abort in cleanup (will happen once in StrictMode dev)
    return () => controller.abort();
  }, [days, filters]);

  // Re-fetch the month grid counts (can be called from children)
  const refreshMonthCounts = async () => {
    const { from, to } = gridRangeFromDays(days);
    if (!from || !to) return;

    try {
      setLoading(true);
      const counts = await fetchEventCounts({ from, to, filters });
      setCountByDay(counts);
    } catch (err) {
      console.error("refreshMonthCounts error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const toKey = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
  };

  const isToday = (d) =>
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  // Open create modal when parent requests it
  useEffect(() => {
    // Only react to explicit create requests (non-zero/non-falsy)
    if (!createRequest) return;
    const today = new Date();
    setMenuDate(today);
    setCreateOpen(true);
    setMenuOpen(false);
  }, [createRequest]);

  return (
    <div className="events-calendar-container">
      <div className="events-calendar-header">
        <button className="events-calendar-nav" onClick={handlePrevMonth} aria-label="Mes anterior">
          <span className="material-icons">chevron_left</span>
        </button>
        <h3 className="events-calendar-title">
          {getMonthName(currentMonth)} {currentYear}
        </h3>
        <button className="events-calendar-nav" onClick={handleNextMonth} aria-label="Mes siguiente">
          <span className="material-icons">chevron_right</span>
        </button>
      </div>

      {loading && <div className="events-calendar-status">Cargando eventos…</div>}

      <div className="events-calendar-grid">
        {weekDays.map((day) => (
          <div key={day} className="events-calendar-weekday">
            {day}
          </div>
        ))}

        {days.map(({ date, currentMonth: inMonth }, idx) => {
          const key = toKey(date);
          const c = countByDay[key] || 0;

          const isLocked = Object.prototype.hasOwnProperty.call(lockedMap, key);
          return (
            <div
              key={idx}
              className="events-calendar-day-container"
              onClick={() => { if (!isLocked) openDayModal(date); }}
              onContextMenu={(e) => openMenuAt(e, date, c > 0)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !isLocked && openDayModal(date)}
            >
              <div
                className={`events-calendar-day ${inMonth ? "current" : "other"} ${isLocked ? 'locked' : ''}`}
              >
                <span className={`day-number ${isToday(date) ? "today" : ""}`}>
                  {date.getDate()}
                </span>
                {isLocked && (
                  <div className="locked-tooltip">
                    {lockedMap[key] ? String(lockedMap[key]) : 'Bloqueado'}
                  </div>
                )}
                {c > 0 && (
                  <span
                    className={`event-badge ${c > 9 ? "double" : ""}`}
                    title={`${c} evento(s)`}
                  >
                    {c}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modalOpen && (
        <DaysEventsModal
          open={modalOpen}
          date={selectedDate}
          filters={filters || {}}
          onClose={closeModal}
          onCountsChanged={refreshMonthCounts}
        />
      )}

      {/* Day quick menu */}
      {menuOpen && menuDate && (
        <div className="calendar-day-menu" style={{ left: menuPos.x, top: menuPos.y }} onClick={(e)=>e.stopPropagation()}>
          {!Object.prototype.hasOwnProperty.call(lockedMap, toKey(menuDate)) && (
            <>
              <button className="calendar-day-menu-item" onClick={() => { setMenuOpen(false); setCreateOpen(true); }}>
                + Agregar nuevo evento
              </button>
              {(countByDay[toKey(menuDate)] || 0) > 0 && (
                <button className="calendar-day-menu-item" onClick={() => { setMenuOpen(false); openDayModal(menuDate); }}>
                  Ver detalle del día
                </button>
              )}
              {(countByDay[toKey(menuDate)] || 0) === 0 && (
                <button className="calendar-day-menu-item forbid" onClick={() => { setMenuOpen(false); setLockDialog({ open: true, date: menuDate, reason: '' }); }}>
                  Bloquear día
                </button>
              )}
            </>
          )}
          {Object.prototype.hasOwnProperty.call(lockedMap, toKey(menuDate)) && (
            <button className="calendar-day-menu-item" onClick={() => { setMenuOpen(false); requestUnlockDate(menuDate); }}>
              Desbloquear día
            </button>
          )}
        </div>
      )}

      {/* Create new event modal */}
      {createOpen && menuDate && (
        <EventEditModal
          eventId={null}
          createNew={true}
          initialDate={menuDate}
          onClose={(updated)=>{ setCreateOpen(false); if(updated){ refreshMonthCounts(); } }}
        />
      )}

      {/* Lock day dialog */}
      {lockDialog.open && lockDialog.date && (
        <div className="calendar-lock-backdrop" onClick={() => setLockDialog({ open:false, date:null, reason:'' })}>
          <div className="calendar-lock-modal" style={{ left: menuPos.x, top: menuPos.y }} onClick={(e)=>e.stopPropagation()}>
            <div className="calendar-lock-modal-title">Bloquear día {toDMY(lockDialog.date)}</div>
            <textarea
              className="calendar-lock-modal-textarea"
              placeholder="Motivo (opcional)"
              value={lockDialog.reason}
              onChange={(e)=>setLockDialog((s)=>({ ...s, reason: e.target.value }))}
            />
            <div className="calendar-lock-modal-footer">
              <button className="calendar-day-menu-item pink" onClick={() => setLockDialog({ open:false, date:null, reason:'' })}>
                Cancelar
              </button>
              <button
                className="calendar-day-menu-item pink"
                onClick={async () => {
                  try {
                    const day = toISODate(lockDialog.date);
                    await lockDate({ day, reason: lockDialog.reason });
                    setLockedMap((m)=>({ ...m, [day]: lockDialog.reason || '' }));
                  } catch (e) {
                    alert(e.message || 'No se pudo bloquear el día');
                  } finally {
                    setLockDialog({ open:false, date:null, reason:'' });
                  }
                }}
              >
                Bloquear
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

export default Calendar;
